// Message tracking functionality
// This module handles email tracking and analytics

import { 
  createMailgunClient, 
  formatEventsParams, 
  createGetRequest, 
  handleRateLimit,
  createRateLimitedRequest,
  type EventsRequestParams 
} from './client';
import { rateLimitedRequest, rateLimitedBulkRequests } from './rateLimiting';
import { 
  query, 
  where, 
  collection, 
  getDocs, 
  doc, 
  setDoc 
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { OutreachCategory } from '../../../types/outreach';
import { OUTREACH_DOC_PATH } from '../utils/constants';
import { convertDoc } from '../utils/helpers';
import { addOutreachItem } from '../firestore/crud';
import type { 
  MessageTrackingData, 
  EnhancedMessageTrackingData,
  MessageCorrelation,
  MessageCorrelationNotFoundError,
  CorrelationDataIncompleteError,
  CorrelationStorageError,
  MailgunEvent, 
  MessageDetails, 
  MailgunEventsResponse
} from '../types';
import { 
  sanitizeEnhancedMessageTrackingData,
  sanitizeString,
  isValidTimestamp 
} from '../utils/helpers';
import { 
  getHighestPriorityStatus,
  isFailureEvent,
  formatMailgunTimestamp,
  aggregateMessageEvents,
  updateMessageWithNewEvents,
  groupEventsByMessageId,
  batchAggregateMessageEvents,
  type AggregatedMessageData
} from '../../../utils/mailgunTransformers';

/**
 * Get message events from Mailgun API for a specific message ID
 * 
 * @param messageId The Mailgun message ID to get events for
 * @returns Array of Mailgun events for the message
 */
export const getMailgunMessageEvents = async (
  messageId: string
): Promise<MailgunEvent[]> => {
  try {
    const client = createMailgunClient();
    
    // Format parameters for events API
    const params: EventsRequestParams = {
      'message-id': messageId,
      limit: 300, // Maximum allowed by Mailgun
      ascending: 'no', // Most recent first
      pretty: 'yes'
    };

    const formattedParams = formatEventsParams(params);
    const requestConfig = createGetRequest('/events', Object.fromEntries(formattedParams));

    console.log(`Fetching events for message ID: ${messageId}`);
    
    // Use the new rate limiting system
    const response = await rateLimitedRequest(
      () => client.request<MailgunEventsResponse>(requestConfig),
      `events_${messageId}`
    );
    
    if (response.data && response.data.items) {
      console.log(`Found ${response.data.items.length} events for message ${messageId}`);
      return response.data.items;
    } else {
      console.warn(`No events found for message ID: ${messageId}`);
      return [];
    }
  } catch (error) {
    console.error(`Error getting message events for ${messageId}:`, error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to get message events: ${error.message}`);
    }
    throw new Error('Unknown error occurred while fetching message events');
  }
};

/**
 * Get detailed message information from Mailgun API
 * 
 * @param messageId The Mailgun message ID to get details for
 * @returns Detailed message information
 */
export const getMailgunMessageDetails = async (
  messageId: string
): Promise<MessageDetails> => {
  try {
    console.log(`Fetching message details for message ID: ${messageId}`);
    
    // First, get events to find the storage URL
    const events = await getMailgunMessageEvents(messageId);
    
    if (events.length === 0) {
      throw new Error(`No events found for message ID: ${messageId}`);
    }

    // Find an event with storage information
    const eventWithStorage = events.find(event => 
      event.storage && event.storage.url
    );

    if (!eventWithStorage || !eventWithStorage.storage?.url) {
      throw new Error(`No storage URL found for message ID: ${messageId}`);
    }

    // Extract the storage URL and make a request to get message details
    const storageUrl = eventWithStorage.storage.url;
    console.log(`Fetching message content from storage URL: ${storageUrl}`);

    // Use the new rate limiting system
    const client = createMailgunClient();
    const response = await rateLimitedRequest(
      () => client.get(storageUrl),
      `details_${messageId}`
    );
    
    const messageData = response.data;
    
    // Transform the raw message data to our MessageDetails interface
    const messageDetails: MessageDetails = {
      messageId: messageData['Message-Id'] || messageId,
      recipient: messageData.To || eventWithStorage.recipient,
      sender: messageData.From || '',
      subject: messageData.Subject || '',
      bodyHtml: messageData['body-html'] || undefined,
      bodyText: messageData['body-plain'] || undefined,
      timestamp: messageData.Date || new Date().toISOString(),
      size: messageData.Size || 0,
      tags: eventWithStorage.tags || [],
      campaigns: eventWithStorage.campaigns || [],
      deliveryStatus: {
        status: getDeliveryStatusFromEvents(events),
        description: getLatestDeliveryDescription(events),
        code: getLatestDeliveryCode(events)
      },
      events: events,
      attachments: messageData.attachments ? messageData.attachments.map((att: any) => ({
        filename: att.filename || att.name || 'unknown',
        contentType: att['content-type'] || att.contentType || 'application/octet-stream',
        size: att.size || 0
      })) : []
    };

    console.log(`Successfully retrieved message details for ${messageId}`);
    return messageDetails;
  } catch (error) {
    console.error(`Error getting message details for ${messageId}:`, error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to get message details: ${error.message}`);
    }
    throw new Error('Unknown error occurred while fetching message details');
  }
};

/**
 * Helper function to determine delivery status from events
 * Uses the centralized event type mapping utilities
 */
const getDeliveryStatusFromEvents = (events: MailgunEvent[]): "accepted" | "delivered" | "failed" | "pending" => {
  const status = getHighestPriorityStatus(events);
  
  // Map application status back to delivery status format
  switch (status) {
    case 'failed':
      return 'failed';
    case 'delivered':
    case 'opened':
    case 'clicked':
      return 'delivered';
    case 'sent':
      return 'accepted';
    default:
      return 'pending';
  }
};

/**
 * Helper function to get the latest delivery description from events
 */
const getLatestDeliveryDescription = (events: MailgunEvent[]): string | undefined => {
  const sortedEvents = events.sort((a, b) => b.timestamp - a.timestamp);
  const latestEvent = sortedEvents.find(e => e['delivery-status']?.message);
  return latestEvent?.['delivery-status']?.message;
};

/**
 * Helper function to get the latest delivery code from events
 */
const getLatestDeliveryCode = (events: MailgunEvent[]): number | undefined => {
  const sortedEvents = events.sort((a, b) => b.timestamp - a.timestamp);
  const latestEvent = sortedEvents.find(e => e['delivery-status']?.code);
  return latestEvent?.['delivery-status']?.code;
};

/**
 * Transform Mailgun events to MessageTrackingData format
 * Uses the new centralized message event aggregation system
 * 
 * @param events Array of Mailgun events
 * @param messageId The message ID
 * @param subject The message subject
 * @returns Transformed message tracking data
 */
export const transformEventsToTrackingData = (
  events: MailgunEvent[],
  messageId: string,
  subject: string
): MessageTrackingData => {
  if (events.length === 0) {
    // Return basic structure for empty events
    return {
      id: messageId,
      recipient: '',
      subject: subject,
      status: 'pending',
      timestamp: new Date().toISOString()
    };
  }

  // Use the new aggregation system to get comprehensive tracking data
  const aggregatedData = aggregateMessageEvents(events);
  
  // Convert aggregated data to the basic MessageTrackingData format
  return {
    id: aggregatedData.id,
    recipient: aggregatedData.recipient,
    subject: subject, // Use provided subject as it may be more accurate
    status: aggregatedData.status,
    timestamp: aggregatedData.timestamp,
    openedAt: aggregatedData.openedAt,
    clickedAt: aggregatedData.clickedAt,
    error: aggregatedData.error
  };
};

/**
 * Transform Mailgun events to EnhancedMessageTrackingData format
 * Uses the new centralized message event aggregation system
 * 
 * @param events Array of Mailgun events
 * @param messageId The message ID
 * @param subject The message subject
 * @returns Enhanced message tracking data with additional fields
 */
export const transformEventsToEnhancedTrackingData = (
  events: MailgunEvent[],
  messageId: string,
  subject: string
): EnhancedMessageTrackingData => {
  if (events.length === 0) {
    // Return basic enhanced structure for empty events
    return {
      id: messageId,
      recipient: '',
      subject: subject,
      status: 'pending',
      timestamp: new Date().toISOString(),
      mailgunMessageId: messageId,
      deliveredAt: undefined,
      bouncedAt: undefined,
      complainedAt: undefined,
      unsubscribedAt: undefined,
      bounceReason: undefined,
      userAgent: undefined,
      clientInfo: undefined,
      geolocation: undefined
    };
  }

  // Use the new aggregation system to get comprehensive tracking data
  const aggregatedData = aggregateMessageEvents(events);
  
  // Convert aggregated data to the EnhancedMessageTrackingData format
  const enhancedData: EnhancedMessageTrackingData = {
    id: aggregatedData.id,
    recipient: aggregatedData.recipient,
    subject: subject, // Use provided subject as it may be more accurate
    status: aggregatedData.status,
    timestamp: aggregatedData.timestamp,
    openedAt: aggregatedData.openedAt,
    clickedAt: aggregatedData.clickedAt,
    error: aggregatedData.error,
    mailgunMessageId: aggregatedData.mailgunMessageId,
    deliveredAt: aggregatedData.deliveredAt,
    bouncedAt: aggregatedData.bouncedAt,
    complainedAt: aggregatedData.complainedAt,
    unsubscribedAt: aggregatedData.unsubscribedAt,
    bounceReason: aggregatedData.bounceReason,
    userAgent: aggregatedData.userAgent,
    clientInfo: aggregatedData.clientInfo,
    geolocation: aggregatedData.geolocation
  };

  // Sanitize and validate the enhanced data
  const sanitizedData = sanitizeEnhancedMessageTrackingData(enhancedData);
  
  if (!sanitizedData) {
    // If sanitization fails, fall back to basic enhanced structure
    return {
      id: messageId,
      recipient: events[0]?.recipient || '',
      subject: subject,
      status: getHighestPriorityStatus(events),
      timestamp: events[0] ? formatMailgunTimestamp(events[0].timestamp) : new Date().toISOString(),
      mailgunMessageId: messageId,
      deliveredAt: undefined,
      bouncedAt: undefined,
      complainedAt: undefined,
      unsubscribedAt: undefined,
      bounceReason: undefined,
      userAgent: undefined,
      clientInfo: undefined,
      geolocation: undefined
    };
  }

  return sanitizedData;
};

/**
 * Batch process multiple messages using the new aggregation system
 * This function demonstrates the power of the message event aggregation utilities
 * 
 * @param events Array of Mailgun events from potentially multiple messages
 * @param existingTrackingData Optional map of existing tracking data by message ID
 * @returns Map of message ID to aggregated tracking data
 */
export const batchProcessMessageEvents = async (
  events: MailgunEvent[],
  existingTrackingData?: Map<string, AggregatedMessageData>
): Promise<Map<string, AggregatedMessageData>> => {
  try {
    console.log(`Batch processing ${events.length} events using aggregation system`);
    
    // Use the new batch aggregation system
    const aggregatedResults = batchAggregateMessageEvents(events, existingTrackingData);
    
    console.log(`Successfully aggregated data for ${aggregatedResults.size} messages`);
    return aggregatedResults;
  } catch (error) {
    console.error('Error in batch processing message events:', error);
    throw new Error(`Failed to batch process message events: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Update existing message tracking data with new events
 * This function demonstrates incremental updates using the aggregation system
 * 
 * @param existingData Current aggregated message data
 * @param newEvents New Mailgun events to incorporate
 * @returns Updated aggregated message data
 */
export const updateExistingMessageTracking = (
  existingData: AggregatedMessageData,
  newEvents: MailgunEvent[]
): AggregatedMessageData => {
  try {
    console.log(`Updating existing message ${existingData.mailgunMessageId} with ${newEvents.length} new events`);
    
    // Use the new update system
    const updatedData = updateMessageWithNewEvents(existingData, newEvents);
    
    console.log(`Successfully updated message ${existingData.mailgunMessageId}. New status: ${updatedData.status}`);
    return updatedData;
  } catch (error) {
    console.error(`Error updating message tracking for ${existingData.mailgunMessageId}:`, error);
    throw new Error(`Failed to update message tracking: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Refresh campaign tracking data by fetching all messages associated with a campaign
 * 
 * @param campaignId The campaign ID to refresh tracking data for
 * @returns Array of message tracking data for all messages in the campaign
 */
export const refreshCampaignTracking = async (
  campaignId: string
): Promise<MessageTrackingData[]> => {
  try {
    console.log(`Refreshing campaign tracking data for campaign: ${campaignId}`);
    
    const client = createMailgunClient();
    
    // Step 1: Query events API to find all messages associated with this campaign
    const params: EventsRequestParams = {
      limit: 300, // Maximum allowed by Mailgun
      ascending: 'no', // Most recent first
      pretty: 'yes'
    };

    const formattedParams = formatEventsParams(params);
    const requestConfig = createGetRequest('/events', Object.fromEntries(formattedParams));

    console.log(`Fetching events for campaign: ${campaignId}`);
    
    // Use rate limiting for the initial events fetch
    const response = await rateLimitedRequest(
      () => client.request<MailgunEventsResponse>(requestConfig),
      `campaign_events_${campaignId}`
    );
    
    let allEvents: MailgunEvent[] = [];
    if (response.data && response.data.items) {
      allEvents = response.data.items;
      console.log(`Found ${allEvents.length} total events`);
    } else {
      console.warn(`No events found for campaign: ${campaignId}`);
      return [];
    }

    // Step 2: Filter events by campaign and extract unique message IDs
    const campaignEvents = allEvents.filter(event => 
      event.campaigns && event.campaigns.includes(campaignId)
    );

    console.log(`Found ${campaignEvents.length} events for campaign ${campaignId}`);

    if (campaignEvents.length === 0) {
      console.warn(`No events found for campaign: ${campaignId}`);
      return [];
    }

    // Extract unique message IDs
    const messageIds = new Set<string>();
    const messageSubjects = new Map<string, string>();

    campaignEvents.forEach(event => {
      const messageId = event.message?.headers?.['message-id'];
      if (messageId) {
        messageIds.add(messageId);
        // Try to get subject from event data if available
        // Note: Subject might not be available in all event types
      }
    });

    console.log(`Found ${messageIds.size} unique messages in campaign ${campaignId}`);

    // Step 3: Create request functions for bulk processing
    const messageTrackingRequestFns = Array.from(messageIds).map((messageId) => {
      return async (): Promise<MessageTrackingData | null> => {
        try {
          // Get events for this specific message
          const messageEvents = await getMailgunMessageEvents(messageId);
          
          if (messageEvents.length === 0) {
            console.warn(`No events found for message: ${messageId}`);
            return null;
          }

          // Try to get subject from message details if not available in events
          let subject = 'Unknown Subject';
          try {
            const messageDetails = await getMailgunMessageDetails(messageId);
            subject = messageDetails.subject || subject;
          } catch (error) {
            console.warn(`Could not fetch message details for ${messageId}, using default subject`);
          }

          // Transform events to MessageTrackingData format
          const trackingData = transformEventsToTrackingData(messageEvents, messageId, subject);
          
          console.log(`Successfully processed message ${messageId} with status: ${trackingData.status}`);
          return trackingData;
        } catch (error) {
          console.error(`Error processing message ${messageId}:`, error);
          return null; // Return null for failed messages, they'll be filtered out
        }
      };
    });

    // Step 4: Execute all requests with enhanced rate limiting and concurrency control
    console.log(`Processing ${messageTrackingRequestFns.length} messages with enhanced rate limiting...`);
    
    const results = await rateLimitedBulkRequests(messageTrackingRequestFns, {
      requestsPerMinute: 60, // More conservative for bulk operations
      maxConcurrent: 3, // Lower concurrency for bulk operations
      enableLogging: process.env.NODE_ENV === 'development'
    });
    
    // Step 5: Filter successful results and log any failures
    const trackingData: MessageTrackingData[] = [];
    let successCount = 0;
    let failureCount = 0;

    results.forEach((result, index) => {
      if (result instanceof Error) {
        failureCount++;
        console.error(`Failed to process message at index ${index}:`, result.message);
      } else if (result !== null) {
        trackingData.push(result);
        successCount++;
      } else {
        failureCount++;
      }
    });

    console.log(`Campaign tracking refresh completed for ${campaignId}:`);
    console.log(`- Successfully processed: ${successCount} messages`);
    console.log(`- Failed to process: ${failureCount} messages`);
    console.log(`- Total tracking data entries: ${trackingData.length}`);

    return trackingData;
  } catch (error) {
    console.error(`Error refreshing campaign tracking for ${campaignId}:`, error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to refresh campaign tracking: ${error.message}`);
    }
    throw new Error('Unknown error occurred while refreshing campaign tracking');
  }
};

/**
 * Get message tracking data from Mailgun (enhanced version)
 * This function now uses real Mailgun API calls instead of mock data
 *
 * @param campaignId ID of the campaign to get tracking data for
 * @returns Array of message tracking data
 */
export const getMessageTrackingData = async (
  campaignId: string
): Promise<MessageTrackingData[]> => {
  try {
    console.log(`Getting message tracking data for campaign: ${campaignId}`);
    
    // Use the new refreshCampaignTracking function for real data
    return await refreshCampaignTracking(campaignId);
  } catch (error) {
    console.error("Error getting message tracking data:", error);
    
    // Fallback to mock data if the API call fails
    console.warn("Falling back to mock data due to API error");
    return [
      {
        id: "msg_1",
        recipient: "recipient@example.com",
        subject: "Check out our new music",
        status: "delivered",
        timestamp: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: "msg_2",
        recipient: "another@example.com",
        subject: "Collaboration opportunity",
        status: "opened",
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        openedAt: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: "msg_3",
        recipient: "third@example.com",
        subject: "Featured on your playlist",
        status: "clicked",
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        openedAt: new Date(Date.now() - 43200000).toISOString(),
        clickedAt: new Date(Date.now() - 21600000).toISOString(),
      },
    ];
  }
};

/**
 * Get enhanced message tracking data from Mailgun with additional fields
 * 
 * @param campaignId ID of the campaign to get enhanced tracking data for
 * @returns Array of enhanced message tracking data
 */
export const getEnhancedMessageTrackingData = async (
  campaignId: string
): Promise<EnhancedMessageTrackingData[]> => {
  try {
    console.log(`Getting enhanced message tracking data for campaign: ${campaignId}`);
    
    // Use the enhanced version of refreshCampaignTracking
    return await refreshCampaignTrackingEnhanced(campaignId);
  } catch (error) {
    console.error("Error getting enhanced message tracking data:", error);
    
    // Fallback to mock enhanced data if the API call fails
    console.warn("Falling back to mock enhanced data due to API error");
    return [
      {
        id: "msg_1",
        recipient: "recipient@example.com",
        subject: "Check out our new music",
        status: "delivered",
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        mailgunMessageId: "<msg_1@example.com>",
        deliveredAt: new Date(Date.now() - 3600000).toISOString(),
        clientInfo: {
          name: "Chrome",
          os: "Windows",
          device: "Desktop"
        },
        geolocation: {
          country: "US",
          region: "CA",
          city: "San Francisco"
        }
      },
      {
        id: "msg_2",
        recipient: "another@example.com",
        subject: "Collaboration opportunity",
        status: "opened",
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        openedAt: new Date(Date.now() - 3600000).toISOString(),
        mailgunMessageId: "<msg_2@example.com>",
        deliveredAt: new Date(Date.now() - 7200000).toISOString(),
        userAgent: "Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X)",
        clientInfo: {
          name: "Safari",
          os: "iOS",
          device: "Mobile"
        },
        geolocation: {
          country: "US",
          region: "NY",
          city: "New York"
        }
      }
    ];
  }
};

/**
 * Refresh campaign tracking data with enhanced information
 * 
 * @param campaignId The campaign ID to refresh enhanced tracking data for
 * @returns Array of enhanced message tracking data for all messages in the campaign
 */
export const refreshCampaignTrackingEnhanced = async (
  campaignId: string
): Promise<EnhancedMessageTrackingData[]> => {
  try {
    console.log(`Refreshing enhanced campaign tracking data for campaign: ${campaignId}`);
    
    const client = createMailgunClient();
    
    // Step 1: Query events API to find all messages associated with this campaign
    const params: EventsRequestParams = {
      limit: 300, // Maximum allowed by Mailgun
      ascending: 'no', // Most recent first
      pretty: 'yes'
    };

    const formattedParams = formatEventsParams(params);
    const requestConfig = createGetRequest('/events', Object.fromEntries(formattedParams));

    console.log(`Fetching events for enhanced campaign tracking: ${campaignId}`);
    
    // Use rate limiting for the initial events fetch
    const response = await rateLimitedRequest(
      () => client.request<MailgunEventsResponse>(requestConfig),
      `enhanced_campaign_events_${campaignId}`
    );
    
    let allEvents: MailgunEvent[] = [];
    if (response.data && response.data.items) {
      allEvents = response.data.items;
      console.log(`Found ${allEvents.length} total events for enhanced tracking`);
    } else {
      console.warn(`No events found for enhanced campaign tracking: ${campaignId}`);
      return [];
    }

    // Step 2: Filter events by campaign and extract unique message IDs
    const campaignEvents = allEvents.filter(event => 
      event.campaigns && event.campaigns.includes(campaignId)
    );

    console.log(`Found ${campaignEvents.length} events for enhanced campaign ${campaignId}`);

    if (campaignEvents.length === 0) {
      console.warn(`No events found for enhanced campaign: ${campaignId}`);
      return [];
    }

    // Extract unique message IDs
    const messageIds = new Set<string>();

    campaignEvents.forEach(event => {
      const messageId = event.message?.headers?.['message-id'];
      if (messageId) {
        messageIds.add(messageId);
      }
    });

    console.log(`Found ${messageIds.size} unique messages for enhanced campaign ${campaignId}`);

    // Step 3: Create request functions for enhanced bulk processing
    const enhancedTrackingRequestFns = Array.from(messageIds).map((messageId) => {
      return async (): Promise<EnhancedMessageTrackingData | null> => {
        try {
          // Get events for this specific message
          const messageEvents = await getMailgunMessageEvents(messageId);
          
          if (messageEvents.length === 0) {
            console.warn(`No events found for enhanced message: ${messageId}`);
            return null;
          }

          // Try to get subject from message details if not available in events
          let subject = 'Unknown Subject';
          try {
            const messageDetails = await getMailgunMessageDetails(messageId);
            subject = messageDetails.subject || subject;
          } catch (error) {
            console.warn(`Could not fetch message details for enhanced ${messageId}, using default subject`);
          }

          // Transform events to EnhancedMessageTrackingData format
          const enhancedTrackingData = transformEventsToEnhancedTrackingData(messageEvents, messageId, subject);
          
          console.log(`Successfully processed enhanced message ${messageId} with status: ${enhancedTrackingData.status}`);
          return enhancedTrackingData;
        } catch (error) {
          console.error(`Error processing enhanced message ${messageId}:`, error);
          return null; // Return null for failed messages, they'll be filtered out
        }
      };
    });

    // Step 4: Execute all requests with enhanced rate limiting and concurrency control
    console.log(`Processing ${enhancedTrackingRequestFns.length} messages for enhanced tracking...`);
    
    const results = await rateLimitedBulkRequests(enhancedTrackingRequestFns, {
      requestsPerMinute: 50, // More conservative for enhanced operations
      maxConcurrent: 3, // Lower concurrency for enhanced operations
      enableLogging: process.env.NODE_ENV === 'development'
    });
    
    // Step 5: Filter successful results and log any failures
    const enhancedTrackingData: EnhancedMessageTrackingData[] = [];
    let successCount = 0;
    let failureCount = 0;

    results.forEach((result, index) => {
      if (result instanceof Error) {
        failureCount++;
        console.error(`Failed to process enhanced message at index ${index}:`, result.message);
      } else if (result !== null) {
        enhancedTrackingData.push(result);
        successCount++;
      } else {
        failureCount++;
      }
    });

    console.log(`Enhanced campaign tracking refresh completed for ${campaignId}:`);
    console.log(`- Successfully processed: ${successCount} enhanced messages`);
    console.log(`- Failed to process: ${failureCount} enhanced messages`);
    console.log(`- Total enhanced tracking data entries: ${enhancedTrackingData.length}`);

    return enhancedTrackingData;
  } catch (error) {
    console.error(`Error refreshing enhanced campaign tracking for ${campaignId}:`, error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to refresh enhanced campaign tracking: ${error.message}`);
    }
    throw new Error('Unknown error occurred while refreshing enhanced campaign tracking');
  }
};

// Message Correlation Functions
// These functions handle the storage and retrieval of correlations between campaign IDs and Mailgun message IDs

/**
 * Store a message correlation in Firestore
 * 
 * @param correlation The correlation data to store
 * @returns The ID of the stored correlation record
 */
export const storeMessageCorrelation = async (
  correlation: Omit<MessageCorrelation, 'id'>
): Promise<string> => {
  try {
    // Validate required fields
    const requiredFields: (keyof typeof correlation)[] = ['campaignId', 'recipientEmail', 'mailgunMessageId', 'timestamp'];
    const missingFields = requiredFields.filter(field => !correlation[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Correlation data incomplete. Missing fields: ${missingFields.join(', ')}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(correlation.recipientEmail)) {
      throw new Error('Correlation data incomplete. Missing fields: recipientEmail (invalid format)');
    }

    // Validate timestamp format
    if (!isValidTimestamp(correlation.timestamp)) {
      throw new Error('Correlation data incomplete. Missing fields: timestamp (invalid format)');
    }

    console.log(`Storing message correlation for campaign ${correlation.campaignId}, message ${correlation.mailgunMessageId}`);
    
    const correlationId = await addOutreachItem(
      OutreachCategory.MESSAGE_CORRELATIONS,
      correlation
    );

    console.log(`Successfully stored message correlation with ID: ${correlationId}`);
    return correlationId;
  } catch (error) {
    console.error('Error storing message correlation:', error);
    
    if (error instanceof Error) {
      throw new Error(`Correlation storage error during store: ${error.message}`);
    }
    throw new Error('Correlation storage error during store: Unknown error occurred');
  }
};

/**
 * Get all message IDs for a specific campaign
 * 
 * @param campaignId The campaign ID to get message IDs for
 * @returns Array of Mailgun message IDs associated with the campaign
 */
export const getMessageIdsForCampaign = async (
  campaignId: string
): Promise<string[]> => {
  try {
    if (!campaignId) {
      throw new Error(`Message correlation not found for campaignId: ${campaignId}`);
    }

    console.log(`Getting message IDs for campaign: ${campaignId}`);

    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${OutreachCategory.MESSAGE_CORRELATIONS}`);
    const q = query(collectionRef, where("campaignId", "==", campaignId));
    const snapshot = await getDocs(q);
    
    const correlations = snapshot.docs.map((doc) => convertDoc<MessageCorrelation>(doc));
    const messageIds = correlations.map(correlation => correlation.mailgunMessageId);
    
    console.log(`Found ${messageIds.length} message IDs for campaign ${campaignId}`);
    return messageIds;
  } catch (error) {
    console.error(`Error getting message IDs for campaign ${campaignId}:`, error);
    
    if (error instanceof Error && error.message.includes('Message correlation not found')) {
      throw error;
    }
    
    // Return empty array for other errors to maintain functionality
    console.warn(`Returning empty array due to error: ${error}`);
    return [];
  }
};

/**
 * Get campaign ID for a specific Mailgun message ID
 * 
 * @param messageId The Mailgun message ID to get campaign ID for
 * @returns The campaign ID associated with the message, or null if not found
 */
export const getCampaignIdForMessageId = async (
  messageId: string
): Promise<string | null> => {
  try {
    if (!messageId) {
      throw new Error(`Message correlation not found for messageId: ${messageId}`);
    }

    console.log(`Getting campaign ID for message: ${messageId}`);

    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${OutreachCategory.MESSAGE_CORRELATIONS}`);
    const q = query(collectionRef, where("mailgunMessageId", "==", messageId));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.warn(`No correlation found for message ID: ${messageId}`);
      return null;
    }

    const correlation = convertDoc<MessageCorrelation>(snapshot.docs[0]);
    console.log(`Found campaign ID ${correlation.campaignId} for message ${messageId}`);
    return correlation.campaignId;
  } catch (error) {
    console.error(`Error getting campaign ID for message ${messageId}:`, error);
    
    if (error instanceof Error && error.message.includes('Message correlation not found')) {
      throw error;
    }
    
    // Return null for other errors to maintain functionality
    console.warn(`Returning null due to error: ${error}`);
    return null;
  }
};

/**
 * Get all correlations for a specific recipient email
 * 
 * @param recipientEmail The recipient email to get correlations for
 * @returns Array of message correlations for the recipient
 */
export const getCorrelationsByRecipient = async (
  recipientEmail: string
): Promise<MessageCorrelation[]> => {
  try {
    if (!recipientEmail) {
      throw new Error(`Message correlation not found for recipientEmail: ${recipientEmail}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      throw new Error('Correlation data incomplete. Missing fields: recipientEmail (invalid format)');
    }

    console.log(`Getting correlations for recipient: ${recipientEmail}`);

    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${OutreachCategory.MESSAGE_CORRELATIONS}`);
    const q = query(collectionRef, where("recipientEmail", "==", recipientEmail));
    const snapshot = await getDocs(q);
    
    const correlations = snapshot.docs.map((doc) => convertDoc<MessageCorrelation>(doc));
    
    console.log(`Found ${correlations.length} correlations for recipient ${recipientEmail}`);
    return correlations;
  } catch (error) {
    console.error(`Error getting correlations for recipient ${recipientEmail}:`, error);
    
    if (error instanceof Error && 
        (error.message.includes('Message correlation not found') || 
         error.message.includes('Correlation data incomplete'))) {
      throw error;
    }
    
    // Return empty array for other errors to maintain functionality
    console.warn(`Returning empty array due to error: ${error}`);
    return [];
  }
};

/**
 * Get a specific correlation by campaign ID and recipient email
 * 
 * @param campaignId The campaign ID
 * @param recipientEmail The recipient email
 * @returns The message correlation if found, null otherwise
 */
export const getCorrelationByCampaignAndRecipient = async (
  campaignId: string,
  recipientEmail: string
): Promise<MessageCorrelation | null> => {
  try {
    if (!campaignId || !recipientEmail) {
      const missingFields = [];
      if (!campaignId) missingFields.push('campaignId');
      if (!recipientEmail) missingFields.push('recipientEmail');
      throw new Error(`Correlation data incomplete. Missing fields: ${missingFields.join(', ')}`);
    }

    console.log(`Getting correlation for campaign ${campaignId} and recipient ${recipientEmail}`);

    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${OutreachCategory.MESSAGE_CORRELATIONS}`);
    const q = query(
      collectionRef, 
      where("campaignId", "==", campaignId),
      where("recipientEmail", "==", recipientEmail)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.warn(`No correlation found for campaign ${campaignId} and recipient ${recipientEmail}`);
      return null;
    }

    const correlation = convertDoc<MessageCorrelation>(snapshot.docs[0]);
    console.log(`Found correlation for campaign ${campaignId} and recipient ${recipientEmail}`);
    return correlation;
  } catch (error) {
    console.error(`Error getting correlation for campaign ${campaignId} and recipient ${recipientEmail}:`, error);
    
    if (error instanceof Error && error.message.includes('Correlation data incomplete')) {
      throw error;
    }
    
    // Return null for other errors to maintain functionality
    console.warn(`Returning null due to error: ${error}`);
    return null;
  }
};